import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import publicApiClient from '@services/api/publicClient'
import { storageURL, mediaUrl } from '@config'
import { encodeId, decodeId } from '@utils/idCipher'

const mapBanner = (raw) => ({
  id: raw.post_id,
  titre: raw.libele || 'Bannière',
  description: raw.description || '',
  image_url: raw.fichiers?.[0]?.chemin ? mediaUrl(raw.fichiers[0].chemin) : null,
  lien_url: raw.siteWeb || null,
  views: raw.vues || 0,
  likes: raw.post_likes_count ?? 0,
  created_at: raw.created_at,
  category: raw.category?.libele_categorie || null,
})

function StaffBannerDetailPage() {
  const { id: encodedId } = useParams()
  const id = decodeId(encodedId) // id réel pour les appels API
  const navigate = useNavigate()
  const [banner, setBanner] = useState(null)
  const [loading, setLoading] = useState(true)
  const [related, setRelated] = useState([])
  const [imgLoaded, setImgLoaded] = useState(false)
  const incrementedRef = useRef(null)

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true)
        setImgLoaded(false)
        const result = await publicApiClient.get(`/posts/${id}`)
        const raw = result.data || result
        if (!raw?.post_id) { setBanner(null); return }
        setBanner(mapBanner(raw))

        const relResult = await publicApiClient.get('/posts/type', { params: { type: 3, staff_only: 1 } })
        const relData = relResult.data || []
        setRelated(relData.filter(b => String(b.post_id) !== String(id)).slice(0, 6).map(mapBanner))
      } catch {
        setBanner(null)
      } finally {
        setLoading(false)
      }
    }
    if (id) fetch()
  }, [id])

  useEffect(() => {
    if (!id || incrementedRef.current === id) return
    incrementedRef.current = id
    publicApiClient.post(`/posts/${id}/view`).catch(() => {})
  }, [id])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-14 h-14 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Chargement...</p>
        </div>
      </div>
    )
  }

  if (!banner) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Bannière introuvable</h2>
          <button onClick={() => navigate(-1)} className="mt-4 px-6 py-2 bg-[#D4AF37] text-white rounded-full font-medium hover:bg-[#c09d2a] transition-colors">
            Retour
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ─── Hero : affiche plein cadre ─── */}
      <div className="relative w-full bg-[#0a0a0a]">

        {/* Bouton retour flottant */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 z-20 flex items-center gap-2 bg-black/40 backdrop-blur-sm text-white px-4 py-2 rounded-full hover:bg-black/60 transition-colors font-medium text-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Retour
        </button>

        {/* Badge sponsorisé */}
        <div className="absolute top-4 right-4 z-20 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          Sponsorisé
        </div>

        {/* Affiche — object-contain pour ne rien couper */}
        {banner.image_url && (
          <div className="flex justify-center items-center w-full" style={{ minHeight: 420, maxHeight: 680, background: '#0a0a0a' }}>
            <img
              src={banner.image_url}
              alt={banner.titre}
              onLoad={() => setImgLoaded(true)}
              className={`max-w-full max-h-[680px] w-auto h-auto object-contain transition-opacity duration-700 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
              style={{ display: 'block', margin: '0 auto' }}
            />
          </div>
        )}

        {/* Dégradé bas léger pour transition vers le blanc */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-gray-50 to-transparent" />

        {/* Titre en bas du hero */}
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-6 md:px-10">
          {banner.category && (
            <span className="inline-block bg-[#D4AF37]/90 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
              {banner.category}
            </span>
          )}
          <h1 className="text-2xl md:text-4xl font-bold text-white drop-shadow-lg leading-tight">
            {banner.titre}
          </h1>
        </div>
      </div>

      {/* ─── Corps ─── */}
      <div className="w-full lg:w-[88%] xl:w-[80%] mx-auto px-4 py-8 space-y-6">

        {/* Stats + CTA */}
        <div className="bg-white rounded-2xl shadow-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                <svg className="w-4.5 h-4.5 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-400">Vues</p>
                <p className="font-bold text-[#001F3F]">{banner.views.toLocaleString('fr-FR')}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                <svg className="w-4.5 h-4.5 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-400">Likes</p>
                <p className="font-bold text-[#001F3F]">{banner.likes.toLocaleString('fr-FR')}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                <svg className="w-4.5 h-4.5 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-400">Publié le</p>
                <p className="font-bold text-[#001F3F]">
                  {banner.created_at ? new Date(banner.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                </p>
              </div>
            </div>
          </div>

          {banner.lien_url && (
            <a
              href={banner.lien_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-white rounded-full font-bold shadow-md hover:shadow-xl hover:scale-105 transition-all text-sm whitespace-nowrap"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Visiter le site
            </a>
          )}
        </div>

        {/* Description */}
        {banner.description && (
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-lg font-bold text-[#001F3F] mb-3 flex items-center gap-2">
              <span className="w-1 h-6 bg-[#D4AF37] rounded-full inline-block" />
              Description
            </h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">{banner.description}</p>
          </div>
        )}

        {/* Bannières similaires */}
        {related.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-[#001F3F] mb-4 flex items-center gap-2">
              <span className="w-1 h-6 bg-[#D4AF37] rounded-full inline-block" />
              Autres bannières sponsorisées
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((b) => (
                <button
                  key={b.id}
                  onClick={() => { navigate(`/staff-banner/${encodeId(b.id)}`); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 hover:border-[#D4AF37] transition-all group text-left"
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={b.image_url}
                      alt={b.titre}
                      className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={e => { e.target.style.display = 'none' }}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                    <div className="absolute top-2 right-2 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      PRO
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-[#001F3F] group-hover:text-[#D4AF37] transition-colors line-clamp-1 mb-1">{b.titre}</h3>
                    {b.description && <p className="text-xs text-gray-500 line-clamp-2 mb-2">{b.description}</p>}
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        {b.views.toLocaleString()} vues
                      </span>
                      <span>{b.created_at ? new Date(b.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default StaffBannerDetailPage
